class ShippingBarThreshold extends HTMLElement {
  constructor() {
    super();
    this.onCartChanged = this.onCartChanged.bind(this);
    this.onProductFormChange = this.onProductFormChange.bind(this);
    this.isRefreshing = false;
    this.abortController = null;
    this._productInfo = null;
    this._variantChangeUnsubscriber = null;
  }

  connectedCallback() {
    if (!this.hasAttribute("data-pdp-threshold")) return;
    document.addEventListener("cart:changed", this.onCartChanged);

    this._setupLivePreview();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:changed", this.onCartChanged);

    if (this._boundQtyInput) {
      this._boundQtyInput.removeEventListener(
        "change",
        this.onProductFormChange,
      );
      this._boundQtyInput.removeEventListener(
        "input",
        this.onProductFormChange,
      );
      this._boundQtyInput = null;
    }
    if (this._docListener) {
      document.removeEventListener("change", this._docListener, true);
      document.removeEventListener("input", this._docListener, true);
      this._docListener = null;
    }
    if (this._variantChangeUnsubscriber) {
      this._variantChangeUnsubscriber();
      this._variantChangeUnsubscriber = null;
    }
    this._productInfo = null;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  _setupLivePreview() {
    // Parse variant weights map
    try {
      this._variantWeights = JSON.parse(this.dataset.variantWeights || "{}");
    } catch (e) {
      this._variantWeights = {};
    }
    this._baseFrozenWeight =
      parseFloat(this.dataset.baseFrozenWeight || "0") || 0;
    this._frozenGoal = parseFloat(this.dataset.frozenGoal || "4000") || 4000;
    this._messageTemplate = this.dataset.thresholdMessage || "";
    this._messageSubinfo = this.dataset.thresholdMessageSubinfo || "";
    this._messageGoalReached = this.dataset.thresholdMessageGoalReached || "";
    this._messageGoalReachedInCart =
      this.dataset.thresholdMessageGoalReachedInCart || "";

    // Scope to the surrounding product-info section (PDP form)
    this._productInfo =
      this.closest("product-info") || this.closest('[id^="ProductInfo-"]');

    this._bindFormListeners();

    // Re-bind if variant change re-renders inputs
    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      this._variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        () => {
          // Re-bind in case quantity-input was replaced
          this._bindFormListeners();
          this._updatePreview();
        },
      );
    }

    // Fallback: listen at document level (capture phase) so we always catch updates
    this._docListener = (event) => {
      const t = event.target;
      if (!t || !this._productInfo) return;
      if (!this._productInfo.contains(t)) return;
      if (
        t.matches?.(
          '.quantity__input, input[name="quantity"], input[name="id"], select[name="id"]',
        ) ||
        t.closest?.("variant-selects, variant-radios")
      ) {
        this._updatePreview();
      }
    };
    document.addEventListener("change", this._docListener, true);
    document.addEventListener("input", this._docListener, true);

    // Initial render reflects any pre-filled quantity
    this._updatePreview();
  }

  _bindFormListeners() {
    if (!this._productInfo) return;
    const qtyInput = this._productInfo.querySelector(
      '.quantity__input, input[name="quantity"]',
    );
    if (qtyInput && qtyInput !== this._boundQtyInput) {
      qtyInput.addEventListener("change", this.onProductFormChange);
      qtyInput.addEventListener("input", this.onProductFormChange);
      this._boundQtyInput = qtyInput;
    }
  }

  onProductFormChange() {
    this._updatePreview();
  }

  _getSelectedVariantWeight() {
    if (!this._productInfo || !this._variantWeights) return 0;
    const variantInput = this._productInfo.querySelector('[name="id"]');
    if (!variantInput) return 0;
    const id = variantInput.value;
    const weight = this._variantWeights[id];
    return typeof weight === "number" ? weight : 0;
  }

  _getQuantity() {
    if (!this._productInfo) return 0;
    const qtyInput = this._productInfo.querySelector(
      '.quantity__input, input[name="quantity"]',
    );
    if (!qtyInput) return 0;
    const qty = parseInt(qtyInput.value, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 0;
  }

  _updatePreview() {
    const bar = this.querySelector(".active__bar-wrapper");
    if (!bar) return;

    const variantWeight = this._getSelectedVariantWeight();
    const qty = this._getQuantity();

    // The PDP bar always previews "cart + form selection" so the currently
    // shown quantity (including the default 1 on page load) is counted in
    // the calculation. The "in cart" copy is only used when the cart by
    // itself already meets the goal — see the goal-text logic below.
    const previewTotal = this._baseFrozenWeight + variantWeight * qty;

    let progress = (previewTotal / this._frozenGoal) * 100;
    if (!Number.isFinite(progress) || progress < 0) progress = 0;
    if (progress > 100) progress = 100;

    bar.style.setProperty("--current-progress", progress + "%");
    bar.dataset.currentWidth = progress.toFixed(2);

    // Update the goal text
    const goalStep = this.querySelector(".goals__step");
    if (goalStep) {
      const remaining = Math.max(this._frozenGoal - previewTotal, 0);
      const remainingKg = (remaining / 1000).toLocaleString(
        document.documentElement.lang || "de-DE",
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      );
      const goalKg = (this._frozenGoal / 1000).toLocaleString(
        document.documentElement.lang || "de-DE",
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      );

      if (previewTotal < this._frozenGoal) {
        const main = this._messageTemplate.replace(
          "[remaining_weight_kg]",
          remainingKg,
        );
        let html = main;
        if (this._messageSubinfo) {
          html += ` <span>${this._messageSubinfo}</span>`;
        }
        goalStep.innerHTML = html;
      } else {
        // Pick the right "goal reached" message:
        // - If the cart itself already meets the goal, use the "in cart" copy
        //   (e.g. "4 kg erreicht.").
        // - Otherwise this is a PDP mockup preview, so use the preview copy
        //   (e.g. "4 kg erreicht, wenn zum Warenkorb hinzugefügt").
        const cartMeetsGoal = this._baseFrozenWeight >= this._frozenGoal;
        const template =
          cartMeetsGoal && this._messageGoalReachedInCart
            ? this._messageGoalReachedInCart
            : this._messageGoalReached;
        if (template) {
          goalStep.innerHTML = template.replace("[frozen_goal_kg]", goalKg);
        }
      }
    }
  }

  async onCartChanged() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) {
      console.warn("Missing data-product-url on shipping-bar-threshold");
      return;
    }

    if (this.isRefreshing) return;
    this.isRefreshing = true;

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const response = await fetch(productUrl, {
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const updatedThreshold = doc.querySelector(
        `shipping-bar-threshold[data-pdp-threshold][data-product-url="${productUrl}"]`,
      );

      if (!updatedThreshold) {
        console.warn(
          "Updated PDP shipping threshold not found in fetched HTML",
        );
        return;
      }

      if (!this.isConnected) return;

      this.replaceWith(updatedThreshold);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Failed to refresh PDP shipping threshold", error);
    } finally {
      this.isRefreshing = false;
      this.abortController = null;
    }
  }
}

if (!customElements.get("shipping-bar-threshold")) {
  customElements.define("shipping-bar-threshold", ShippingBarThreshold);
}
